import { Component, computed, effect, inject, Injector, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DateAdapter, MatRipple } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationError, Router, RouterModule, RouterOutlet } from '@angular/router';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { BtnComponent, DialogService, IDialogConfig, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslateService } from '@ngx-translate/core';
import 'app/core/extensions/object.extensions';
import 'app/core/extensions/string.extensions';
import { ErrorHelper } from 'app/core/helpers/error.helper';
import { populateTranslateValidation } from 'app/core/helpers/form-validation/form-validation.helper';
import { ILocationTrial } from 'app/core/helpers/location-trial.helper';
import { populateTranslateNumberHelper } from 'app/core/helpers/number/number.helper';
import { effectBlur, parseStringToSearch } from 'app/core/helpers/util';
import { IDataPagination } from 'app/core/models/api/data';
import { ISystemFeature, ISystemMessage, IUser } from 'app/core/models/user/user';
import { AccessGroupService } from 'app/core/services/access-group/access-group.service';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { ClinicService } from 'app/core/services/clinic/clinic.service';
import { CommunicationService } from 'app/core/services/communication/communication.service';
import { MoneyService } from 'app/core/services/money/money.service';
import { UserDeviceService } from 'app/core/services/user/user-device.service';
import { UserService } from 'app/core/services/user/user.service';
import { KiwidSocketService } from 'app/core/socket/kiwid-socket.service';
import { filter, first, forkJoin, last, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EAuthenticationStorage, ELocalStorage } from './core/models/authentication/authentication';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';
import { BaseComponent } from './layout/common/base/base.component';
import { populateTranslateGeneral } from 'app/core/helpers/general-translate/general-translate.helper';
import { populateTranslateFormDynamic } from './core/helpers/form-dynamic/form-dynamic.helper';
import { FirebaseMessagingService } from 'app/core/services/firebase-messaging/firebase-messaging.service';
import { effectQueryParams, eventCurrentURL } from 'app/core/helpers/router.helper';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EventDetectorService } from 'app/core/helpers/event-detector.helper';
import { environment } from '../environments/environment';

type IForceReloadSystem = {
    title: string;
    description: string;
    buttonConfirm: string;
    url?: string;
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet, RouterModule, MatProgressSpinner, MatRipple, BtnComponent],
})
export class AppComponent extends BaseComponent {
    private readonly clinicService = inject(ClinicService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly kiwidSocketService = inject(KiwidSocketService);
    private readonly accessGroupService = inject(AccessGroupService);
    private readonly communicationService = inject(CommunicationService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly moneyService = inject(MoneyService);
    private readonly toastrService = inject(ToastrService);
    private readonly userDeviceService = inject(UserDeviceService);
    private readonly userService = inject(UserService);
    private readonly dateAdapter = inject(DateAdapter);
    private readonly firebaseMessagingService = inject(FirebaseMessagingService);
    private readonly _fuseSplashScreenService = inject(FuseSplashScreenService);
    private readonly dialogService = inject(DialogService);
    private readonly deviceDetectorService = inject(DeviceDetectorService);
    private readonly eventDetectorService = inject(EventDetectorService);
    readonly _translateService = inject(TranslateService);
    readonly title = inject(Title);
    private injector = inject(Injector);

    protected isLoading = signal(true);
    private isLoading$ = toObservable(this.isLoading, { injector: this.injector });
    private readonly eventCurrentURL = eventCurrentURL();
    protected readonly enableBottomSheetSocket = signal(false);
    protected readonly socketData = computed(() => {
        const isOffline = this.kiwidSocketService.isOffline();
        const isConnected = this.kiwidSocketService.isConnected();
        const isReconnecting = this.kiwidSocketService.isReconnecting();
        const disableSocket = this.kiwidSocketService.disableSocket();
        const url = this.eventCurrentURL()?.split('?')?.at?.(0) ?? '';
        const blurUrls = [];
        const showBottomSheetUrls = [...blurUrls, '/production/order/detail/'];
        const isLoading = this.isLoading();
        const enableBottomSheetSocket = this.enableBottomSheetSocket();
        const withoutConnectionSocket = !isLoading && !!this.authenticationService.clinicSelected() && (isOffline || !isConnected) && !disableSocket;

        return {
            enableBlur:
                !!this.authenticationService.laboratorySelected() &&
                blurUrls.some((blurUrl) => parseStringToSearch(url).indexOf(parseStringToSearch(blurUrl)) > -1) &&
                (isOffline || !isConnected) &&
                !disableSocket,
            withoutConnection: enableBottomSheetSocket
                ? withoutConnectionSocket
                : showBottomSheetUrls.some((blurUrl) => parseStringToSearch(url).indexOf(parseStringToSearch(blurUrl)) > -1) &&
                  withoutConnectionSocket,
            text: this.translateKey(isReconnecting ? 'SOCKET.RECONNECTING' : isOffline ? 'SOCKET.OFFLINE' : 'SOCKET.DISCONNECT'),
            btnReconnect: {
                enable: !isOffline && !isReconnecting,
                text: this.translateKey('SOCKET.RECONNECT'),
            },
            isReconnecting,
            actions: {
                onReconnect: () => {
                    this.kiwidSocketService.destroySocket();
                    this.kiwidSocketService.isReconnecting.set(true);
                    this.kiwidSocketService.initSocket();
                },
            },
        };
    });
    private reconnectingTimer: NodeJS.Timeout | null = null;
    protected readonly viewReloadSystem = signal<IForceReloadSystem | null>(null);
    protected readonly viewSystemStatus = computed(() => {
        this.authenticationService.user();

        if (environment.type === 'local') {
            return {
                text: 'DEVELOPMENT',
                classes: 'bg-brand-secondary-default dark:bg-brand-secondary-dark text-white dark:text-brand-secondary-lighter',
            };
        }

        return undefined;
    });

    /**
     * Constructor
     */
    constructor() {
        super();
        this.setVhUnit();

        effect(() => {
            const isReconnecting = this.kiwidSocketService.isReconnecting();
            const isConnected = this.kiwidSocketService.isConnected();
            const isOffline = this.kiwidSocketService.isOffline();

            untracked(() => {
                clearTimeout(this.reconnectingTimer);
                this.reconnectingTimer = setTimeout(
                    () => {
                        this.enableBottomSheetSocket.set(isReconnecting || isOffline || !isConnected);
                    },
                    isOffline ? 0 : 50 * 1000,
                );
            });
        });
        effectBlur(
            () => ({
                enabled: this.socketData().enableBlur,
                selectors: ['.cdk-overlay-pane', '.kiwid-base-container'],
                blurClass: 'blur-sm',
            }),
            { injector: this.injector },
        );
        effectBlur(
            () => ({
                enabled: this.socketData().withoutConnection,
                selectors: ['.kiwid-socket-connection'],
                blurClass: 'blur-sm',
            }),
            { injector: this.injector },
        );

        this.subscriptions.add(
            this._translateService.onLangChange.subscribe(({ lang }) => {
                const langLocalStorage = localStorage.getItem(EAuthenticationStorage.LANG) ?? 'pt';
                if (String(lang).trim() !== String(langLocalStorage).trim()) {
                    localStorage.setItem(EAuthenticationStorage.LANG, lang);
                }
                this.dateAdapter.setLocale(lang);
            }),
        );
        this.registerLoading();
        this.registerRouter();
        this.registerLaboratoryIntegrityCheck();

        // Add languages
        this._translateService.addLangs(['pt', 'en', 'es']);

        // Use a language
        const language = localStorage.getItem(EAuthenticationStorage.LANG) ?? 'pt';
        this._translateService.use(language);

        this.moneyService.maskMoney = {
            decimalSeparator: ',',
            thousandSeparator: '.',
            symbol: 'R$',
            isoCode: 'BRL',
        };

        setTimeout(() => {
            this._translateService.setFallbackLang('en');
            this._translateService.setFallbackLang('es');
            this._translateService.setFallbackLang('pt');

            this.registerDefaultData();
            this.registerOnTranslate('APP', portuguese, english, spanish, false);
            populateTranslateValidation(this.baseFuseTranslationLoaderService, this.baseTranslateService);
            populateTranslateFormDynamic(this.baseFuseTranslationLoaderService, this.baseTranslateService);
            populateTranslateNumberHelper(this.baseFuseTranslationLoaderService, this.baseTranslateService);
            populateTranslateGeneral(this.baseFuseTranslationLoaderService, this.baseTranslateService);
        });

        // this.subscriptions.add(
        //     this.router.events.subscribe(event => {
        //         if (event instanceof NavigationEnd) {
        //             this.analytics.logEvent('pageView');
        //         }
        //     })
        // );

        this.registerSocket();
    }

    private registerDefaultData(): void {
        effectQueryParams(
            computed(() => ({
                id: this.authenticationService.user()?.id,
            })),
            ['id'],
            {
                fnc: (parameter) => {
                    if (!!parameter?.id) {
                        if (this.deviceDetectorService.deviceInfo().browser.toLowerCase() === 'safari') {
                            this.safariEnableNotificationPermission();
                            return;
                        }

                        this.notificationPermission();
                    }
                },
            },
            { injector: this.injector },
        );
        if (!!this.authenticationService.token) {
            this.firebaseMessagingService.listenToMessages();

            if (!!this.authenticationService.clinicId()) {
                this.subscriptions.add(
                    forkJoin([this.userService.me(true) as Observable<IUser>, this.clinicService.getAllWithAuth()])
                        .pipe(last())
                        .subscribe({
                            next: ([user, { data: clinics }]) => {
                                this.authenticationService.setUser(user);
                                this.authenticationService.setClinicList(clinics);

                                for (const clinic of clinics) {
                                    if (clinic.id === this.authenticationService.clinicId()) {
                                        this.authenticationService.setClinicSelected(clinic);
                                        this.registerChangeClinic();
                                    }
                                }
                            },
                            error: (error) => {
                                ErrorHelper.setToastrError(this.toastrService, error);
                                this.registerChangeClinic();
                            },
                        }),
                );
                return;
            }

            this.subscriptions.add(
                (this.userService.me(true) as Observable<IUser>).pipe(last()).subscribe({
                    next: (user) => {
                        if (window.location.pathname.search('public') > -1) {
                            this.authenticationService.logout(false);
                        } else {
                            this.authenticationService.setUser(user);
                            this.authenticationService.redirectUser();
                        }
                        this.registerChangeClinic();
                    },
                    error: (error) => {
                        ErrorHelper.setToastrError(this.toastrService, error);
                        this.registerChangeClinic();
                    },
                }),
            );

            return;
        }
        this.registerChangeClinic();
    }

    private safariEnableNotificationPermission(): void {
        const kNotificationEnabled = localStorage.getItem(ELocalStorage.K_DEVICE_NOTIFICATION_ENABLED);
        const kNotificationHidden = localStorage.getItem(ELocalStorage.K_DEVICE_NOTIFICATION_HIDDEN);

        if (!!kNotificationHidden) {
            return;
        }

        if (!!kNotificationEnabled && Notification.permission === 'granted') {
            this.notificationPermission();
            return;
        }

        const toastr = this.toastrService.show({
            durationMilliseconds: null,
            style: 'complete',
            type: 'info',
            icon: 'notifications',
            autoCloseDisabled: true,
            title: this.translateKey('PUSH_NOTIFICATION.REQUEST_PERMISSION.TITLE'),
            message: this.translateKey('PUSH_NOTIFICATION.REQUEST_PERMISSION.MESSAGE'),
            buttons: [
                {
                    id: 1,
                    text: this.translateKey('PUSH_NOTIFICATION.BUTTONS.ALLOW'),
                    type: 'primary',
                },
                {
                    id: 2,
                    text: this.translateKey('PUSH_NOTIFICATION.BUTTONS.CANCEL'),
                    type: 'secondary',
                },
            ],
        });

        this.subscriptions.add(
            this.toastrService.buttonClickObservable.subscribe((button) => {
                if (button.id === 1) {
                    localStorage.removeItem(ELocalStorage.K_DEVICE_NOTIFICATION_HIDDEN);
                    localStorage.setItem(ELocalStorage.K_DEVICE_NOTIFICATION_ENABLED, Date.now().toString());
                    this.notificationPermission();
                } else if (button.id === 2) {
                    this.subscriptions.add(this.userDeviceService.register().subscribe());
                    localStorage.setItem(ELocalStorage.K_DEVICE_NOTIFICATION_HIDDEN, Date.now().toString());
                }
                toastr.close();
            }),
        );
    }

    private notificationPermission(): void {
        this.firebaseMessagingService
            .requestPermission()
            .then((token) => {
                this.subscriptions.add(this.userDeviceService.register(token).subscribe());
            })
            .catch(() => {
                this.subscriptions.add(this.userDeviceService.register().subscribe());
            });
    }

    private setVhUnit(): void {
        const setVhUnit = () => {
            let vh = window.innerHeight;
            let vw = window.innerWidth;
            const barDev = environment.type === 'local' ? 26 : 0;

            document.documentElement.style.setProperty('--kiwid-vh-100', `${vh}px`);
            document.documentElement.style.setProperty('--klib-vh-100', `${vh}px`);

            if (vw > 959) {
                document.documentElement.style.setProperty('--kiwid-vh-content', `${vh - 52 - 54 - barDev}px`);
                document.documentElement.style.setProperty('--klib-vh-content', `${vh - 52 - 54 - barDev}px`);
            } else {
                document.documentElement.style.setProperty('--kiwid-vh-content', `${vh - 54 - barDev}px`);
                document.documentElement.style.setProperty('--klib-vh-content', `${vh - 54 - barDev}px`);
            }
        };

        setVhUnit();

        window.addEventListener('resize', setVhUnit);
    }

    private registerChangeClinic(): void {
        this.subscriptions.add(
            this.authenticationService.clinicSelectedEmit.subscribe((clinic) => {
                if (!!clinic) {
                    const baseRoute = window.location.host;
                    const isAuthOrPublic =
                        parseStringToSearch(this.router.url).indexOf(parseStringToSearch(`${baseRoute}/public`)) > -1 ||
                        parseStringToSearch(this.router.url).indexOf(parseStringToSearch(`${baseRoute}/auth`)) > -1;

                    this.isLoading.set(!isAuthOrPublic);

                    this.subscriptions.add(
                        forkJoin([this.accessGroupService.getUserPermission()])
                            .pipe(last())
                            .subscribe({
                                next: ([{ data: permissions }]) => {
                                    this.authenticationService.setPermissions(permissions);

                                    // Verify has permission customer list
                                    if (this.router.url === '/clinic/user-access') {
                                        this.authenticationService.checkRouteToRedirectUser();
                                    }

                                    this.isLoading.set(false);
                                },
                                error: (error) => {
                                    ErrorHelper.setToastrError(this.toastrService, error);
                                    this.isLoading.set(false);
                                },
                            }),
                    );

                    return;
                }

                this.isLoading.set(false);
            }),
        );
    }

    private openModalSystemFeature(data: IDataPagination<ISystemFeature[]>): void {
        // if (data.meta.pagination.total === 0) {
        //     return;
        // }
        //
        // const config: IModalConfig = {
        //     modalId: 'modal-system-feature',
        //     title: this.translateKey('MODAL.FEATURES.TITLE'),
        //     type: 'custom',
        //     width: '999px',
        //     minHeight: '85vh',
        //     buttons: [
        //         {
        //             type: 'secondary',
        //             id: 1,
        //             text: this.translateKey('MODAL.FEATURES.BUTTONS.READ_LATER'),
        //         },
        //         {
        //             type: 'primary',
        //             id: 2,
        //             text: this.translateKey('MODAL.FEATURES.BUTTONS.OK'),
        //         },
        //         {
        //             type: 'transparent',
        //             id: 3,
        //             text: this.translateKey('MODAL.FEATURES.BUTTONS.READ_ALL'),
        //         },
        //     ],
        //     component: new ComponentPortal<ModalSystemFeatureComponent>(ModalSystemFeatureComponent),
        // };
        // const modal = this.modalService.openModal(config);
        //
        // this.subscriptions.add(
        //     modal.componentInstance.instanceObservable<ModalSystemFeatureComponent>().subscribe((dataSystemFeature) => {
        //         dataSystemFeature.dialog = modal;
        //         dataSystemFeature.initialData = data;
        //     }),
        // );
    }

    private registerLoading(): void {
        this.subscriptions.add(
            this.isLoading$.subscribe((isLoading) => {
                if (isLoading) {
                    this._fuseSplashScreenService.show();
                    return;
                }

                this._fuseSplashScreenService.hide();
            }),
        );
    }

    private registerRouter(): void {
        this.subscriptions.add(
            this.router.events
                .pipe(
                    filter((evt) => evt instanceof NavigationError),
                    map((evt) => evt as NavigationError),
                )
                .subscribe((event) => {
                    const url = event?.target?.url ?? event?.url ?? null;
                    const message = typeof event.error === 'string' ? event.error : event.error.message;

                    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
                    const moduleFailedMessage = /Failed to fetch dynamically imported module: [\d]+/;
                    const moduleFailedMessageString = 'Failed to fetch dynamically imported module:';

                    if (
                        String(!!message ? message : 'KIWID')
                            .toLowerCase()
                            .search(moduleFailedMessageString.toLowerCase()) > -1 ||
                        chunkFailedMessage.test(message) ||
                        moduleFailedMessage.test(message)
                    ) {
                        this.viewReloadSystem.set({
                            title: this.translateKey('MODAL.CHUNK_ERROR.TITLE'),
                            description: this.translateKey('MODAL.CHUNK_ERROR.DESCRIPTION'),
                            buttonConfirm: this.translateKey('MODAL.CHUNK_ERROR.BUTTONS.CONFIRM'),
                            url,
                        });
                        return;
                    }
                }),
        );
    }

    private registerLaboratoryIntegrityCheck(): void {
        this.subscriptions.add(
            this.eventDetectorService.visibilitychange().subscribe(() => {
                if (document.visibilityState !== 'visible' || !!this.viewReloadSystem()) {
                    return;
                }

                if (
                    (!this.authenticationService.token && !this.authenticationService.clinicSelected()) ||
                    !this.authenticationService.clinicSelected()
                ) {
                    return;
                }

                const currentClinicId = this.authenticationService.clinicSelected()?.id ?? null;
                const storedClinicId = this.authenticationService.getStoredClinicId();

                const currentUser = this.authenticationService.user();
                const storedUser = this.authenticationService.getStoredUser();

                if (currentClinicId !== storedClinicId || currentUser?.id !== storedUser?.id) {
                    this.viewReloadSystem.set({
                        title: this.translateKey('MODAL.LABORATORY_CHANGED.TITLE'),
                        description: this.translateKey('MODAL.LABORATORY_CHANGED.DESCRIPTION'),
                        buttonConfirm: this.translateKey('MODAL.LABORATORY_CHANGED.BUTTONS.CONFIRM'),
                    });
                }
            }),
        );
    }

    protected confirmReloadSystem(): void {
        const url = this.viewReloadSystem()?.url ?? '/';
        if (!!url) {
            window.location.href = url ?? '/';
        } else {
            window.location.reload();
        }

        // window.location.href = `//${window.location.host}`;
    }

    private openDialogUpdateSystem(url: string | null): void {
        const config: IDialogConfig = {
            title: this.translateKey('MODAL.CHUNK_ERROR.TITLE'),
            message: this.translateKey('MODAL.CHUNK_ERROR.DESCRIPTION'),
            style: 'warning',
            showCloseButton: false,
            disableBackgroundClose: true,
            buttons: [
                {
                    id: 1,
                    type: 'primary',
                    text: this.translateKey('MODAL.CHUNK_ERROR.BUTTONS.CONFIRM'),
                },
            ],
        };

        const dialog = this.dialogService.openDialog(config);

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(first())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe((button) => {
                            if (button.id === 1) {
                                if (!!url) {
                                    window.location.href = url ?? '/';
                                } else {
                                    window.location.reload();
                                }
                                dialog.close();
                            }
                        }),
                    );
                }),
        );
    }

    private registerSocket(): void {
        this.subscriptions.add(
            this.authenticationService.clinicSelectedEmit.subscribe((clinic) => {
                if (!!clinic) {
                    this.kiwidSocketService.initSocket();
                } else {
                    this.kiwidSocketService.destroySocket();
                }
            }),
        );
        this.subscriptions.add(
            this.kiwidSocketService.onException.subscribe(({ message }) => {
                this.toastrService.show({ type: 'error', message });
            }),
        );
    }
}

export const tippyAppendToModal = () => document.body.getElementsByClassName('cdk-overlay-popover')?.item(0);
export const tippyAppendToSelect = () => document.body.getElementsByClassName('mat-mdc-select-panel')?.item(0);
